import { useCallback, useEffect, useRef, useState } from "react";

// A backlog drains within this many frames, so the reveal never trails far behind the model.
const CATCH_UP_FRAMES = 20;
// The reply is committed even if the reveal has not caught up by then, so a stalled frame loop cannot hang a turn.
const MAX_REVEAL_WAIT_MS = 1500;

export const nextRevealLength = (shown: number, target: number): number => {
  if (shown >= target) return target;
  return Math.min(target, shown + Math.max(1, Math.ceil((target - shown) / CATCH_UP_FRAMES)));
};

// Reveals streamed text a few characters per frame, so bursts of tokens read as steady typing.
export const useSmoothText = (target: string) => {
  const [shown, setShown] = useState("");
  const shownLengthRef = useRef(0);
  const targetRef = useRef(target);
  targetRef.current = target;

  useEffect(() => {
    if (target === "") {
      shownLengthRef.current = 0;
      setShown("");
      return undefined;
    }
    let frame = 0;
    const tick = () => {
      const current = targetRef.current;
      const next = nextRevealLength(shownLengthRef.current, current.length);
      shownLengthRef.current = next;
      setShown(current.slice(0, next));
      if (next < current.length) frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [target]);

  const waitUntilRevealed = useCallback(
    () =>
      new Promise<void>(resolve => {
        const startedAt = Date.now();
        const check = () => {
          const caughtUp = shownLengthRef.current >= targetRef.current.length;
          if (caughtUp || Date.now() - startedAt > MAX_REVEAL_WAIT_MS) resolve();
          else requestAnimationFrame(check);
        };
        check();
      }),
    []
  );

  return { shown, waitUntilRevealed };
};
